import React, { Children } from 'react';
import styles from './array-field.scss';
import groupStyle from '../group.scss';

import ArrayItem from './array-item';
import Button from 'ui/button';
import DotIndicator from 'ui/dot-indicator';
import FieldLabel from 'ui/form/field-label';
import FieldNote from 'ui/form/field-note';
import { FormField } from 'ui/form/form-field';

import { TransitionGroup, Fade, CustomTransition } from 'ui/transition';
import { ICON_ADD, ICON_CHEVRON_LEFT, ICON_CHEVRON_RIGHT, ICON_CLOSE } from 'icons';
import { ArrayFieldProps } from 'ui/form/fields';
import {
  countFormFields,
  getFirstFormField,
  hasChildren,
  isFormField,
  isValue,
  getErrorsFromChildren
} from 'ui/form/utils';
import { formData } from 'ui/form/child-field';

interface ArrayFieldState {}

const TRANSITION = {
  standard: {
    start: { transform: 'translateY(-30px)', opacity: 0 },
    end: { transform: 'scaleX(1)', opacity: 1 }
  },
  carousel: {
    startEnter: { transform: 'translateX(30px)', opacity: 0 },
    endEnter: { transform: 'translateX(0)' },
    startLeave: { transform: 'translateX(-30px)', opacity: 0 },
    endLeave: { transform: 'translateX(-30px)', opacity: 0 }
  }
};

type FieldModel = {
  key: number,
  touched: boolean
};

let fieldId = 1;
let id = 1;

function recursiveGetDefault(children: React.ReactChildren, defaultValue, res = {}) {
  Children.forEach(children, child => {
    if (isFormField(child)) {
      let value = child.props.default || (defaultValue && defaultValue[child.props.name]);
      if (value !== undefined) res[child.props.name] = value;
    } else if (hasChildren(child)) {
      recursiveGetDefault(child.props.children, defaultValue, res);
    }
  });

  return res;
}

@formData
export default class ArrayField extends FormField<ArrayFieldProps, ArrayFieldState> {
  static get defaultValue() {
    return [];
  }

  static getErrors(props: ArrayFieldProps, formData): string | false {
    // Make helper not validate on subtree of formData
    const fakeProps = { ...props, name: false };
    const arr = (isValue(props.value) ? props.value : (formData || {})[props.name]) || [];

    let errors = false;
    if (countFormFields(props.children) === 1) {
      const single = getFirstFormField(props.children);
      if (!single.props.name) {
        for (const i of arr) {
          errors = single.type.getErrors({ ...single.props, value: i }) || errors;
        }

        return errors;
      }
    }

    for (const i of arr) errors = getErrorsFromChildren(fakeProps, i) || errors;

    return errors;
  }

  static getDerivedStateFromProps(
    { children, value = [], defaultValue }: ArrayFieldProps,
    { carouselIndex }: ArrayFieldState
  ) {
    const formFieldCount = countFormFields(children);

    if (carouselIndex < 0) carouselIndex += value.length;
    if (carouselIndex > value.length - 1) carouselIndex -= value.length;

    let nuDefaultValue, singleField;
    if (formFieldCount >= 1) {
      const field = getFirstFormField(children);

      if (formFieldCount === 1 && !field.props.name) {
        // If singe field set default value to be array fields default value
        // or field defined default or filed builtin defaultValue
        nuDefaultValue = field.props.default || defaultValue || field.type.defaultValue;
        singleField = true;
      } else {
        nuDefaultValue = recursiveGetDefault(children, defaultValue);
      }
    }

    return {
      carouselIndex,
      singleField,
      defaultValue: nuDefaultValue
    };
  }

  fieldModels: FieldModel[] = [];
  id = 'array-' + id++;
  items: { [string]: ArrayItem } = {};
  state = {
    singleField: false,
    carouselIndex: 0
  };

  validate(onlyTouch: boolean) {
    let valid = true;
    for (let index in this.items) {
      let item = this.items[index];
      valid &= item.validate();
    }

    return valid;
  }

  getFieldModel(index: number): FieldModel {
    if (!this.fieldModels[index]) {
      this.fieldModels[index] = { key: (fieldId++).toString(32) };
    }
    return this.fieldModels[index];
  }

  getFieldKey(index: number): number {
    return this.getFieldModel(index).key;
  }

  touched(index: number) {
    this.getFieldModel(index).touched = true;
  }

  handleDeleteCarousel = () => {
    const { carouselIndex } = this.state;
    const { onChange } = this.props;
    const value = (this.props.value || []).slice();

    value.splice(carouselIndex, 1);
    if (carouselIndex > value.length - 1) this.setState({ carouselIndex: value.length - 1 });

    if (onChange) onChange(value);
  };

  handleClickNext = () => {
    const { value = [] } = this.props;
    let { carouselIndex } = this.state;

    carouselIndex += 1;
    carouselIndex %= value.length;

    this.setState({ carouselIndex });
  };

  handleClickPrev = () => {
    const { value = [] } = this.props;
    let { carouselIndex } = this.state;

    carouselIndex -= 1;
    carouselIndex %= value.length;
    if (carouselIndex < 0) carouselIndex += value.length;

    this.setState({ carouselIndex });
  };

  handleChange = (index, name, fieldValue) => {
    let { value, onChange } = this.props;
    value = value.slice();
    value[index] = this.state.singleField ? fieldValue : { ...value[index], [name]: fieldValue };
    this.touched(index);
    if (onChange) onChange(value);
  };

  handleRemove = (index: number) => {
    const { minLength } = this.props;
    let value = this.props.value.slice();
    value.splice(index, 1);
    this.props.onChange(value);
    this.setState({
      error: value.length < minLength ? `You need to add at least ${minLength} items` : false
    });
  };

  handleAdd = () => {
    let { carousel, maxLength, value } = this.props;
    const { error } = this.state;
    if (value && value.length >= maxLength) {
      this.setState({ error: 'Maximum number of items added' });
    } else {
      let array = (value || []).slice();
      array.push(this.state.defaultValue);

      this.props.onChange(array);
      if (error) this.setState({ error: false });
      if (carousel) this.setState({ carouselIndex: Math.max(0, array.length - 1) });
    }
  };

  /**
   * Moves an element from <code>removeFromIndex</code> to <code>insertAtIndex</code>. 0-indexed.
   */
  handleSort = (removeFromIndex: number, insertAtIndex: number) => {
    let { value, onChange } = this.props;

    if (insertAtIndex < 0) insertAtIndex += value.length;
    if (insertAtIndex > value.length - 1) insertAtIndex -= value.length;

    const array = value.slice();
    const item = array[removeFromIndex];
    array.splice(removeFromIndex, 1);
    array.splice(insertAtIndex, 0, item);

    const fieldKey = this.fieldModels[removeFromIndex];
    this.fieldModels.splice(removeFromIndex, 1);
    this.fieldModels.splice(insertAtIndex, 0, fieldKey);
    value = array;
    this.setState({ carouselIndex: insertAtIndex });
    onChange(value);
  };

  handleItemRef = (index: number, ref) => {
    if (ref) {
      console.log('REF', ref);
      // ArrayItem is wrapped in droptarget and
      // dragsoruce decorator, so need to get component instance
      const dragSource = ref.getDecoratedComponentInstance();
      if (dragSource) this.items[index] = dragSource.getDecoratedComponentInstance();
    } else {
      delete this.items[index];
    }
  };

  render() {
    const {
      carousel,
      children,
      help,
      label,
      noAddRemove,
      noValidation,
      tiny,
      value = []
    } = this.props;
    const { carouselIndex, error } = this.state;
    const formData = this.props.formData || {};

    return (
      <div
        className={styles.root + (carousel ? ' padding ' + groupStyle.root : ' ')}
        onClick={this.focus}
      >
        {label && <FieldLabel label={label} error={error} />}
        <TransitionGroup className={carousel ? styles.carouselHolder : styles.listHolder}>
          {value.map(
            (v, i) =>
              (!carousel || i === carouselIndex) && (
                <CustomTransition
                  className={styles.transition}
                  disabled={carousel}
                  {...TRANSITION[carousel ? 'carousel' : 'standard']}
                  key={this.getFieldKey(i)}
                >
                  <ArrayItem
                    arrayId={this.id}
                    carousel={carousel}
                    formData={formData[i]}
                    index={i}
                    noAddRemove={noAddRemove}
                    noValidation={noValidation}
                    onAdd={this.handleAdd}
                    onChange={this.handleChange}
                    onRemove={this.handleRemove}
                    onSort={this.handleSort}
                    ref={ref => this.handleItemRef(i, ref)}
                    singleField={this.state.singleField}
                    tiny={tiny}
                    value={v}
                  >
                    {children}
                  </ArrayItem>
                </CustomTransition>
              )
          )}
          {!value.length && <Fade className="text-center text-fade margin">No items in list</Fade>}
        </TransitionGroup>
        {error || (help && <FieldNote style={{ margin: 0 }} error={error} note={help} />)}
        {carousel && value.length > 0 && (
          <DotIndicator
            index={carouselIndex + 1}
            max={value.length}
            onClick={a => this.setState({ carouselIndex: a })}
            onSort={this.handleSort}
            style={{ margin: '0 0 10px' }}
          />
        )}
        <div className="layout-row text-center" style={{ width: '100%', position: 'relative' }}>
          {carousel && !!value.length && (
            <div className={styles.leftArrow}>
              <Button
                data-title-bottom="Previous element"
                icon={ICON_CHEVRON_LEFT}
                onClick={this.handleClickPrev}
              />
            </div>
          )}
          {!noAddRemove && (
            <div style={{ marginLeft: 'auto', marginRight: 'auto' }}>
              <Button
                data-title-bottom="New element"
                icon={ICON_ADD}
                role={'formbutton'}
                onClick={this.handleAdd}
                smallCaps
                warning={!!error}
              />
              {carousel && !noAddRemove && !!value.length && (
                <Button
                  data-title-bottom="Delete element"
                  icon={ICON_CLOSE}
                  onClick={this.handleDeleteCarousel}
                  style={{ marginLeft: 10 }}
                />
              )}
            </div>
          )}
          {carousel && !!value.length && (
            <div className={styles.rightArrow}>
              <Button
                data-title-bottom="Next element"
                icon={ICON_CHEVRON_RIGHT}
                onClick={this.handleClickNext}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}
